import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Select from "react-select";
import axios from "axios";
import DatePickerModal from "../../components/DatePickerModal/DatePickerModal";
import "./ReportSapCapisquadra.css";
import Lf23Loader from "../../components/Lf23Loader";
import TabNonAssegnati from "./TabNonAssegnati";
import TabNonAbbinati from "./TabNonAbbinati";
import AnagraficaCapisquadra from "./AnagraficaCapisquadra";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
const BASE = `${API_URL}/report-sap-capisquadra`;
// Convalida e annulla convalida vogliono login + permesso della pagina.
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const selectMenuPortalStyles = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

const VISIBILITA_OPTIONS = [
    { value: "tutti", label: "Tutti" },
    { value: "con", label: "Con servizio" },
    { value: "senza", label: "Senza servizio" },
];
const IGNORATI_OPTIONS = [
    { value: "no", label: "No" },
    { value: "si", label: "Sì" },
];

// Chiave del gruppo delle righe senza caposquadra (non pubblicabili).
const NONE = "__none__";

// Tab della pagina (task 500). La tab attiva sta nell'URL (?tab=...) così i link restano
// condivisibili e il vecchio indirizzo /report-sap-capisquadra/anagrafica ci rimanda qui.
const TABS = [
    { id: "report", label: "Report", sottotitolo: "Turni GECOS delle attività capisquadra per la data selezionata, raggruppati per caposquadra. Le righe convalidate vengono pubblicate su Interacta alle 20:00." },
    { id: "non-assegnati", label: "Non assegnati", sottotitolo: "Servizi che nessun caposquadra ha preso in carico entro 30 minuti dalla fine: spostati in \"Non assegnato\" su Interacta, per community." },
    { id: "non-abbinati", label: "Non abbinati", sottotitolo: "Servizi del file SAP senza riscontro in GECOS. Escono da soli quando su GECOS risultano abbinati; quelli ignorati non ricompaiono nei caricamenti successivi." },
    { id: "anagrafica", label: "Anagrafica", sottotitolo: "Assegna a ogni attività il caposquadra: i suoi servizi vengono pubblicati nella community Interacta del caposquadra." },
];
const TAB_IDS = new Set(TABS.map((t) => t.id));

const toTitleCase = (s) => {
    if (s == null) return s;
    return String(s).toLowerCase().replace(/(?<=^|[^\p{L}])\p{L}/gu, (c) => c.toUpperCase());
};

const formatItalianFromIso = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
};
const formatDataOra = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

// Motivi per cui una riga NON è pubblicabile (vuoto = pubblicabile): serve un caposquadra
// (da Anagrafica), la sua community su Interacta e i campi obbligatori del post.
const missingRequiredFields = (r) => {
    const missing = [];
    if (!r.tipologia) missing.push("Nessun caposquadra assegnato all'attività (vedi Anagrafica)");
    else if (r.community_id == null) missing.push(`Community Interacta "${r.community_name || r.tipologia.label}" non trovata`);
    if (!r.ora_inizio) missing.push("Ora inizio mancante (campo obbligatorio)");
    if (!r.ora_fine) missing.push("Ora fine mancante (campo obbligatorio)");
    if (!r.albero) missing.push("Albero mancante (campo obbligatorio)");
    if (!r.percorso) missing.push("Attività mancante (campo obbligatorio)");
    if (!(r.servizio || r.percorso)) missing.push("Servizio mancante (campo obbligatorio)");
    return missing;
};

const ReportSapCapisquadra = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = TAB_IDS.has(searchParams.get("tab")) ? searchParams.get("tab") : "report";
    const tabCorrente = TABS.find((t) => t.id === tab);

    // L'Anagrafica avvisa se ha modifiche non salvate: cambiando tab (o uscendo) si chiede conferma.
    const [anagraficaDirty, setAnagraficaDirty] = useState(false);
    const puoiLasciareAnagrafica = () =>
        tab !== "anagrafica" || !anagraficaDirty || window.confirm("Ci sono modifiche all'anagrafica non salvate. Uscire comunque?");
    const cambiaTab = (id) => {
        if (id === tab || !puoiLasciareAnagrafica()) return;
        setSearchParams(id === "report" ? {} : { tab: id });
    };

    // Su mobile la barra scorre in orizzontale: la tab attiva va riportata in vista.
    const tabsRef = useRef(null);
    useEffect(() => {
        tabsRef.current?.querySelector(".report-sap-tab.active")
            ?.scrollIntoView({ inline: "nearest", block: "nearest", behavior: "smooth" });
    }, [tab]);

    const [selectedDate, setSelectedDate] = useState("");
    const [righe, setRighe] = useState([]);
    const [loadingRighe, setLoadingRighe] = useState(false);
    const [error, setError] = useState(null);
    const [showFilters, setShowFilters] = useState(true);
    const [selAttivita, setSelAttivita] = useState([]);
    const [selCommessa, setSelCommessa] = useState([]);
    const [visibilita, setVisibilita] = useState("tutti");   // tutti | con | senza
    const [ignorati, setIgnorati] = useState("no");           // no (nascondi ignorate) | si (mostra solo ignorate)

    // Filtri effettivamente applicati alla tabella (aggiornati solo al click su "Applica")
    const [appliedAttivita, setAppliedAttivita] = useState([]);
    const [appliedCommessa, setAppliedCommessa] = useState([]);
    const [appliedVisibilita, setAppliedVisibilita] = useState("tutti");
    const [appliedIgnorati, setAppliedIgnorati] = useState("no");

    const [collapsed, setCollapsed] = useState({}); // per gruppo caposquadra
    const [expanded, setExpanded] = useState(() => new Set()); // rowId espansi (accordion dettaglio)

    const [publishedMap, setPublishedMap] = useState({}); // { [rowId]: postId }
    const [loadingPublished, setLoadingPublished] = useState(false);
    // Task 501: "Pubblica" è diventato "Convalida". busyKey = riga in lavorazione (convalida,
    // annulla convalida, annulla post); busyGroup/busyAll per le convalide in blocco.
    const [busyKey, setBusyKey] = useState(null);
    const [busyGroup, setBusyGroup] = useState(null);
    const [busyAll, setBusyAll] = useState(false);
    const [confirmConvalida, setConfirmConvalida] = useState(null); // { scope, label, items }
    const [esito, setEsito] = useState(null); // { tipo: "ok"|"warn", testo }
    const [pubblicazione, setPubblicazione] = useState(null); // stato del job serale

    // Ignora: una riga che esce dalla vista per via del filtro "Ignorati" non sparisce di colpo,
    // resta un attimo con la spunta e poi si dissolve (rowId -> in uscita). Timer puliti all'uscita.
    // Deve coincidere con la durata dell'animazione report-sap-uscita nel CSS.
    const USCITA_MS = 1200;
    const [uscenti, setUscenti] = useState(() => new Set());
    const timerUscita = useRef(new Map());
    useEffect(() => () => timerUscita.current.forEach(clearTimeout), []);
    const togliUscente = (rowId) => {
        clearTimeout(timerUscita.current.get(rowId));
        timerUscita.current.delete(rowId);
        setUscenti((prev) => { if (!prev.has(rowId)) return prev; const n = new Set(prev); n.delete(rowId); return n; });
    };

    const [exporting, setExporting] = useState(false);

    // Bootstrap: data di default.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${BASE}/default-date`);
                if (!cancelled && res.data?.date) setSelectedDate(res.data.date);
            } catch (err) {
                console.error("Errore default-date:", err);
                if (!cancelled) setError("Errore nel recupero della data di default.");
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const fetchRighe = useCallback(async (date) => {
        if (!date) return;
        setLoadingRighe(true);
        setError(null);
        try {
            const res = await axios.get(`${BASE}/righe`, { params: { date } });
            setRighe(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error("Errore fetch righe:", err);
            setError(err.response?.data?.error || "Errore nel recupero dei turni.");
            setRighe([]);
        } finally {
            setLoadingRighe(false);
        }
    }, []);

    const fetchPublished = useCallback(async (date) => {
        if (!date) return;
        setLoadingPublished(true);
        try {
            const res = await axios.get(`${BASE}/published`, { params: { date } });
            const map = {};
            (Array.isArray(res.data) ? res.data : []).forEach((p) => { if (p?.rowId != null) map[p.rowId] = p.postId; });
            setPublishedMap(map);
        } catch (err) {
            console.error("Errore published:", err);
            setPublishedMap({});
        } finally {
            setLoadingPublished(false);
        }
    }, []);

    // Stato del job serale (ultimo giro + data dell'ultimo SAP, l'unica che il job pubblica).
    const fetchPubblicazione = useCallback(async () => {
        try {
            const res = await axios.get(`${BASE}/pubblicazione-automatica`);
            setPubblicazione(res.data || null);
        } catch (err) {
            console.error("Errore pubblicazione-automatica:", err);
        }
    }, []);

    useEffect(() => {
        if (!selectedDate) return;
        setPublishedMap({});
        setEsito(null);
        fetchRighe(selectedDate);
        fetchPublished(selectedDate);
        fetchPubblicazione();
    }, [selectedDate, fetchRighe, fetchPublished, fetchPubblicazione]);

    const attivitaOptions = useMemo(() => {
        const set = new Set();
        righe.forEach((r) => r.percorso && set.add(r.percorso));
        return Array.from(set).sort().map((v) => ({ value: v, label: v }));
    }, [righe]);

    const commessaOptions = useMemo(() => {
        const set = new Set();
        righe.forEach((r) => r.descrizione_commessa && set.add(r.descrizione_commessa));
        return Array.from(set).sort().map((v) => ({ value: v, label: v }));
    }, [righe]);

    const filteredRighe = useMemo(() => {
        const attSet = new Set(appliedAttivita.map((o) => o.value));
        const comSet = new Set(appliedCommessa.map((o) => o.value));
        return righe.filter((r) => {
            if (appliedVisibilita === "con" && !r.servizio) return false;
            if (appliedVisibilita === "senza" && r.servizio) return false;
            // le righe "in uscita" restano visibili finché finisce la dissolvenza
            if (appliedIgnorati === "no" && r.ignora && !uscenti.has(r.rowId)) return false;
            if (appliedIgnorati === "si" && !r.ignora && !uscenti.has(r.rowId)) return false;
            if (attSet.size && !attSet.has(r.percorso)) return false;
            if (comSet.size && !comSet.has(r.descrizione_commessa)) return false;
            return true;
        });
    }, [righe, appliedVisibilita, appliedIgnorati, appliedAttivita, appliedCommessa, uscenti]);

    const handleApplyFilters = useCallback(() => {
        setAppliedAttivita(selAttivita);
        setAppliedCommessa(selCommessa);
        setAppliedVisibilita(visibilita);
        setAppliedIgnorati(ignorati);
    }, [selAttivita, selCommessa, visibilita, ignorati]);

    const handleResetFilters = useCallback(() => {
        setSelAttivita([]);
        setSelCommessa([]);
        setVisibilita("tutti");
        setIgnorati("no");
        setAppliedAttivita([]);
        setAppliedCommessa([]);
        setAppliedVisibilita("tutti");
        setAppliedIgnorati("no");
    }, []);

    const isPublished = useCallback((rowId) => Object.prototype.hasOwnProperty.call(publishedMap, rowId), [publishedMap]);
    // Convalidata = c'è la convalida e il post non è (ancora) su Interacta.
    const isConvalidata = useCallback((r) => !isPublished(r.rowId) && r.convalida?.stato === "convalidata", [isPublished]);

    // Group-by caposquadra (tipologia): titolo = community Interacta di destinazione (nome del
    // caposquadra senza "Caposquadra"). Senza caposquadra -> gruppo "Senza caposquadra".
    const groups = useMemo(() => {
        const map = new Map();
        for (const r of filteredRighe) {
            const k = r.tipologia ? String(r.tipologia.id) : NONE;
            if (!map.has(k)) {
                map.set(k, {
                    key: k,
                    name: k === NONE ? "Senza caposquadra" : (r.community_name || r.tipologia.label),
                    tipologia: k === NONE ? null : r.tipologia.label,
                    communityMancante: k !== NONE && r.community_id == null,
                    rows: [],
                });
            }
            map.get(k).rows.push(r);
        }
        const arr = [...map.values()].map((g) => ({
            ...g,
            // un post per riga: il totale è il numero di righe del gruppo
            totale: g.rows.length,
            pubblicati: g.rows.filter((r) => isPublished(r.rowId)).length,
            convalidati: g.rows.filter(isConvalidata).length,
        }));
        arr.sort((a, b) => (a.key === NONE ? 1 : b.key === NONE ? -1 : a.name.localeCompare(b.name)));
        return arr;
    }, [filteredRighe, isPublished, isConvalidata]);

    const totalePubblicati = useMemo(() => filteredRighe.filter((r) => isPublished(r.rowId)).length, [filteredRighe, isPublished]);
    const totaleConvalidati = useMemo(() => filteredRighe.filter(isConvalidata).length, [filteredRighe, isConvalidata]);

    const toggleGroup = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

    const handleExportExcel = async () => {
        if (exporting || !selectedDate) return;
        setExporting(true);
        try {
            const items = filteredRighe.map((r) => ({
                codice_itinerario: r.codice_itinerario,
                descrizione_itinerario: r.descrizione_itinerario,
                percorso: r.percorso,
                albero: r.albero,
                descrizione_commessa: r.descrizione_commessa,
                ora_inizio: r.ora_inizio,
                ora_fine: r.ora_fine,
                servizio: r.servizio,
                tipologia: r.tipologia ? r.tipologia.label : "",
                community: r.community_name || "",
                operatori: (r.operatori || []).map(toTitleCase).join(", "),
                automezzi: ((r.targhe && r.targhe.length) ? r.targhe : (r.veicoli_raw || [])).join(", "),
                ignora: !!r.ignora,
                pubblicato: isPublished(r.rowId),
            }));
            const res = await axios.post(`${BASE}/export`, { date: selectedDate, rows: items }, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `report_capisquadra_${selectedDate}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Errore export:", err);
            alert("Errore nell'esportazione Excel.");
        } finally {
            setExporting(false);
        }
    };

    const toggleExpanded = (rowId) => setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
        return next;
    });

    // ---- Convalida (task 501) ----
    // Aggiorna lo stato di convalida delle righe in pagina senza ricaricare i turni da GECOS.
    const impostaConvalida = (rowIds, valore) => {
        const ids = new Set(rowIds);
        setRighe((prev) => prev.map((r) => (ids.has(r.rowId) ? { ...r, convalida: valore } : r)));
    };

    const convalida = async (rowIds) => {
        // Si mandano le righe come le ha la pagina: il backend salva e basta, senza rileggere GECOS.
        const ids = new Set(rowIds);
        const payload = righe.filter((r) => ids.has(r.rowId)).map((r) => ({ rowId: r.rowId, percorso: r.percorso, communityId: r.community_id }));
        const res = await axios.post(`${BASE}/convalida`, { date: selectedDate, righe: payload }, { headers: authHeaders() });
        const { convalidate = 0, saltate = [] } = res.data || {};
        const saltateSet = new Set(saltate.map((x) => x.rowId));
        impostaConvalida(rowIds.filter((id) => !saltateSet.has(id)), { stato: "convalidata", da: localStorage.getItem("username") || null, il: new Date().toISOString() });
        return { convalidate, saltate };
    };

    const handleConvalida = async (row) => {
        if (busyKey) return;
        setBusyKey(row.rowId);
        try {
            const { saltate } = await convalida([row.rowId]);
            if (saltate.length) setEsito({ tipo: "warn", testo: `Non convalidata: ${saltate[0].motivo}` });
        } catch (err) {
            console.error("Errore convalida:", err);
            setEsito({ tipo: "warn", testo: err.response?.data?.error || "Errore nella convalida." });
        } finally {
            setBusyKey(null);
        }
    };

    const handleAnnullaConvalida = async (row) => {
        if (busyKey) return;
        setBusyKey(row.rowId);
        try {
            await axios.post(`${BASE}/annulla-convalida`, { date: selectedDate, rowIds: [row.rowId] }, { headers: authHeaders() });
            impostaConvalida([row.rowId], null);
        } catch (err) {
            console.error("Errore annulla convalida:", err);
            setEsito({ tipo: "warn", testo: err.response?.data?.error || "Errore nell'annullamento della convalida." });
        } finally {
            setBusyKey(null);
        }
    };

    const handleToggleIgnora = async (row) => {
        const nuovo = !row.ignora;
        // Con il filtro attuale la riga uscirà dalla vista? Allora prima la dissolvenza.
        const esce = (appliedIgnorati === "no" && nuovo) || (appliedIgnorati === "si" && !nuovo);
        if (esce) {
            togliUscente(row.rowId);
            setUscenti((prev) => new Set(prev).add(row.rowId));
            timerUscita.current.set(row.rowId, setTimeout(() => togliUscente(row.rowId), USCITA_MS));
        }
        // optimistic update
        setRighe((prev) => prev.map((r) => (r.rowId === row.rowId ? { ...r, ignora: nuovo } : r)));
        try {
            await axios.post(`${BASE}/ignora`, {
                date: selectedDate,
                attivita_id: row.attivita_id,
                ora_inizio: row.ora_inizio,
                ora_fine: row.ora_fine,
                ignora: nuovo,
            });
            // Il backend toglie la convalida a una riga ignorata (se non era già pubblicata).
            if (nuovo && row.convalida && row.convalida.stato !== "pubblicata") impostaConvalida([row.rowId], null);
        } catch (err) {
            console.error("Errore toggle ignora:", err);
            // rollback: la riga torna com'era e resta visibile
            togliUscente(row.rowId);
            setRighe((prev) => prev.map((r) => (r.rowId === row.rowId ? { ...r, ignora: !nuovo } : r)));
            alert(err.response?.data?.error || "Errore nel salvataggio.");
        }
    };

    // "Annulla" su una riga pubblicata: cancella il post da Interacta e la riga torna da convalidare.
    const handleAnnulla = async (row) => {
        if (busyKey) return;
        const rowId = row.rowId;
        const postId = publishedMap[rowId];
        if (postId == null) return;
        setBusyKey(rowId);
        try {
            await axios.delete(`${BASE}/post/${postId}`);
            setPublishedMap((prev) => { const n = { ...prev }; delete n[rowId]; return n; });
            impostaConvalida([rowId], null);
        } catch (err) {
            console.error("Errore annulla:", err);
            alert(err.response?.data?.error || "Errore nell'annullamento del post.");
        } finally {
            setBusyKey(null);
        }
    };

    // Righe che si possono ancora convalidare: campi a posto, non pubblicate, non già convalidate.
    const daConvalidare = (rows) => rows
        .filter((r) => missingRequiredFields(r).length === 0 && !isPublished(r.rowId) && !r.convalida)
        .map((r) => r.rowId);

    const askConvalidaGroup = (group) => setConfirmConvalida({
        scope: "group", groupKey: group.key, label: `${group.tipologia} (community ${group.name})`, items: daConvalidare(group.rows),
    });
    const askConvalidaAll = () => setConfirmConvalida({
        scope: "all", label: "tutti i caposquadra", items: daConvalidare(filteredRighe),
    });

    const handleConfirmConvalida = async () => {
        if (!confirmConvalida || !selectedDate) return;
        const { scope, groupKey, items } = confirmConvalida;
        setConfirmConvalida(null);
        if (scope === "all") setBusyAll(true); else setBusyGroup(groupKey);
        try {
            const { convalidate, saltate } = await convalida(items);
            const parti = [`${convalidate} ${convalidate === 1 ? "servizio convalidato" : "servizi convalidati"}: saranno pubblicati alle 20:00`];
            if (saltate.length) parti.push(`${saltate.length} non convalidati (${[...new Set(saltate.map((x) => x.motivo))].join(" · ")})`);
            setEsito({ tipo: saltate.length ? "warn" : "ok", testo: parti.join(" · ") });
        } catch (err) {
            console.error("Errore convalida in blocco:", err);
            setEsito({ tipo: "warn", testo: err.response?.data?.error || "Errore nella convalida." });
        } finally {
            if (scope === "all") setBusyAll(false); else setBusyGroup(null);
        }
    };

    const nessunaDaConvalidare = daConvalidare(filteredRighe).length === 0;
    const busyBulk = busyAll || busyGroup !== null;
    // Il job delle 20:00 pubblica solo l'ultimo SAP caricato: se si guarda un'altra data va detto.
    const dataJob = pubblicazione?.dataSap || null;
    const altraData = !!(dataJob && selectedDate && dataJob !== selectedDate);
    const ultimoGiro = pubblicazione?.ultimo || null;

    return (
        <div className="report-sap-page">
            <div className="page-header">
                <div className="header-content">
                    <button className="back-button" onClick={() => { if (puoiLasciareAnagrafica()) navigate("/automazione"); }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        Indietro
                    </button>
                    <div className="header-title">
                        <h2>Report Capisquadra</h2>
                        <p className="header-subtitle">{tabCorrente.sottotitolo}</p>
                    </div>
                </div>
            </div>

            <div className="report-sap-tabs" ref={tabsRef} role="tablist">
                {TABS.map((t) => (
                    <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
                        className={`report-sap-tab ${tab === t.id ? "active" : ""}`} onClick={() => cambiaTab(t.id)}>
                        {t.id === "report" && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                        )}
                        {t.id === "non-assegnati" && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                        )}
                        {t.id === "anagrafica" && (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                        )}
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "non-assegnati" && <TabNonAssegnati />}
            {tab === "non-abbinati" && <TabNonAbbinati />}
            {tab === "anagrafica" && <AnagraficaCapisquadra onDirtyChange={setAnagraficaDirty} />}

            {tab === "report" && (<>
            <div className="report-sap-toolbar">
                <button
                    type="button"
                    className={`toggle-filters-btn ${showFilters ? "active" : ""}`}
                    onClick={() => setShowFilters((v) => !v)}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                    </svg>
                    {showFilters ? "Nascondi Filtri" : "Mostra Filtri"}
                </button>

                <div className="report-sap-toolbar-right">
                    <div className="report-sap-summary" title="Convalidati (escono alle 20:00) e pubblicati su Interacta, sul totale dei turni mostrati">
                        <span className="report-sap-summary-value">{totaleConvalidati}</span>
                        <span className="report-sap-summary-label">convalidati ·</span>
                        <span className="report-sap-summary-value">{totalePubblicati}</span>
                        <span className="report-sap-summary-sep">/</span>
                        <span className="report-sap-summary-total">{filteredRighe.length}</span>
                        <span className="report-sap-summary-label">pubblicati</span>
                    </div>
                    <button
                        type="button"
                        className="report-sap-publish-all-btn"
                        onClick={askConvalidaAll}
                        disabled={filteredRighe.length === 0 || nessunaDaConvalidare || busyBulk || loadingPublished}
                        title={nessunaDaConvalidare ? "Niente da convalidare: già convalidati, pubblicati o con dati mancanti" : "Convalida tutti i turni mostrati: saranno pubblicati alle 20:00"}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                        {busyAll ? "Convalido..." : "Convalida tutto"}
                    </button>
                    <button
                        type="button"
                        className="report-sap-export-btn"
                        onClick={handleExportExcel}
                        disabled={exporting || filteredRighe.length === 0}
                        title="Esporta in Excel le righe attualmente visibili (con flag Ignorato e Pubblicato)"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        {exporting ? "Esportazione…" : "Esporta Excel"}
                    </button>
                </div>
            </div>

            {showFilters && (
                <div className="filter-panel">
                    <div className="filter-grid">
                        <div className="filter-group">
                            <label>Data</label>
                            <DatePickerModal value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} label="Seleziona data" />
                        </div>
                        <div className="filter-group">
                            <label>Attività</label>
                            <Select
                                isMulti
                                placeholder="Tutte le attività"
                                options={attivitaOptions}
                                value={selAttivita}
                                onChange={(s) => setSelAttivita(s || [])}
                                classNamePrefix="filter-select"
                                closeMenuOnSelect={false}
                                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                styles={selectMenuPortalStyles}
                            />
                        </div>
                        <div className="filter-group">
                            <label>Commessa</label>
                            <Select
                                isMulti
                                placeholder="Tutte le commesse"
                                options={commessaOptions}
                                value={selCommessa}
                                onChange={(s) => setSelCommessa(s || [])}
                                classNamePrefix="filter-select"
                                closeMenuOnSelect={false}
                                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                styles={selectMenuPortalStyles}
                            />
                        </div>
                        <div className="filter-group">
                            <label>Visibilità</label>
                            <Select
                                options={VISIBILITA_OPTIONS}
                                value={VISIBILITA_OPTIONS.find((o) => o.value === visibilita)}
                                onChange={(o) => setVisibilita(o?.value || "tutti")}
                                isSearchable={false}
                                classNamePrefix="filter-select"
                                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                styles={selectMenuPortalStyles}
                            />
                        </div>
                        <div className="filter-group">
                            <label>Ignorati</label>
                            <Select
                                options={IGNORATI_OPTIONS}
                                value={IGNORATI_OPTIONS.find((o) => o.value === ignorati)}
                                onChange={(o) => setIgnorati(o?.value || "no")}
                                isSearchable={false}
                                classNamePrefix="filter-select"
                                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                styles={selectMenuPortalStyles}
                            />
                        </div>
                    </div>
                    <div className="report-sap-filter-actions">
                        <button type="button" className="report-sap-btn-secondary" onClick={handleResetFilters}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                <path d="M3 3v5h5" />
                            </svg>
                            Reset
                        </button>
                        <button type="button" className="report-sap-btn-primary" onClick={handleApplyFilters}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Applica
                        </button>
                    </div>
                </div>
            )}

            {pubblicazione && (
                <div className={`report-sap-job-info ${altraData ? "report-sap-job-info--warn" : ""}`}>
                    <span>
                        {pubblicazione.attiva ? "Pubblicazione automatica alle 20:00" : "Pubblicazione automatica spenta in questo ambiente"}
                        {dataJob && <> delle righe convalidate del <strong>{formatItalianFromIso(dataJob)}</strong> (ultimo SAP caricato)</>}
                    </span>
                    {altraData && <span>· stai guardando il {formatItalianFromIso(selectedDate)}: le convalide di questa data non vengono pubblicate</span>}
                    {ultimoGiro && (
                        <span className={ultimoGiro.esito === "errore" ? "report-sap-job-info-errore" : ""} title={ultimoGiro.errore || undefined}>
                            · ultimo giro {formatDataOra(ultimoGiro.terminato_il || ultimoGiro.avviato_il)}
                            {ultimoGiro.contatori ? `: ${ultimoGiro.contatori.pubblicate || 0} pubblicati${ultimoGiro.contatori.errori ? `, ${ultimoGiro.contatori.errori} in errore` : ""}` : ""}
                            {ultimoGiro.esito === "errore" ? " (con errori)" : ""}
                        </span>
                    )}
                </div>
            )}

            {esito && (
                <div className={`report-sap-na-esito report-sap-na-esito--${esito.tipo}`} role="status">
                    <span>{esito.testo}</span>
                    <button type="button" onClick={() => setEsito(null)} aria-label="Chiudi">×</button>
                </div>
            )}

            {error && <div className="report-sap-error" role="alert">{error}</div>}

            {loadingRighe && righe.length === 0 && (
                <Lf23Loader testo="Caricamento turni in corso..." />
            )}

            {!loadingRighe && filteredRighe.length === 0 && !error && (
                <div className="report-sap-empty">
                    {selectedDate ? `Nessun turno trovato per il ${formatItalianFromIso(selectedDate)}.` : "Seleziona una data."}
                </div>
            )}

            <div className="report-sap-list">
                {groups.map((group) => {
                    const isCollapsed = !!collapsed[group.key];
                    const groupBusy = busyGroup === group.key;
                    const groupDaConvalidare = daConvalidare(group.rows).length;
                    return (
                        <section key={group.key} className={`report-sap-localita-card ${group.key === NONE ? "report-sap-localita-card--none" : ""}`}>
                            <div className="report-sap-localita-header">
                                <button type="button" className="report-sap-localita-header-toggle" onClick={() => toggleGroup(group.key)} aria-expanded={!isCollapsed}>
                                    <div className="report-sap-localita-title">
                                        <svg className={`report-sap-chevron ${isCollapsed ? "collapsed" : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="6 9 12 15 18 9" />
                                        </svg>
                                        <div className="report-sap-localita-heading">
                                            <span className="report-sap-localita-name">{group.name}</span>
                                            {group.tipologia && (
                                                <span className="report-sap-localita-tipologie">
                                                    {group.tipologia}
                                                    {group.communityMancante && <span className="report-sap-localita-warning"> · ⚠ community non trovata su Interacta</span>}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="report-sap-localita-kpi">
                                        <span className="report-sap-localita-count">{group.convalidati} convalidati · {group.pubblicati} pubblicati / {group.totale} post</span>
                                    </div>
                                </button>
                                {group.key === NONE ? (
                                    <button type="button" className="report-sap-localita-link-btn" onClick={() => cambiaTab("anagrafica")}>
                                        Assegna in Anagrafica →
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="report-sap-localita-publish-btn"
                                        onClick={() => askConvalidaGroup(group)}
                                        disabled={groupBusy || busyBulk || loadingPublished || groupDaConvalidare === 0}
                                        title={group.communityMancante ? "Community non trovata su Interacta" : groupDaConvalidare === 0 ? "Niente da convalidare" : "Convalida tutti i turni del caposquadra: saranno pubblicati alle 20:00"}
                                    >
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                                        {groupBusy ? "Convalido..." : "Convalida caposquadra"}
                                    </button>
                                )}
                            </div>

                            {!isCollapsed && (
                                <div className="report-sap-table-wrap">
                                    <table className="report-sap-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: "3%" }} className="report-sap-expand-col" aria-label="Dettagli" />
                                                <th style={{ width: "18%" }}>Attività</th>
                                                <th style={{ width: "18%" }}>Albero</th>
                                                <th style={{ width: "15%" }}>Descrizione commessa</th>
                                                <th style={{ width: "8%" }}>Ora inizio</th>
                                                <th style={{ width: "8%" }}>Ora fine</th>
                                                <th style={{ width: "10%" }}>Servizio</th>
                                                <th style={{ width: "15%" }} className="report-sap-action-col">Interacta</th>
                                                <th style={{ width: "5%" }} className="report-sap-action-col">Ignora</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {group.rows.map((r) => {
                                                const published = isPublished(r.rowId);
                                                const rowBusy = busyKey === r.rowId || groupBusy || busyAll;
                                                const conv = r.convalida;
                                                const isExpanded = expanded.has(r.rowId);
                                                const missing = missingRequiredFields(r);
                                                const canPublish = missing.length === 0;
                                                return (
                                                    <React.Fragment key={`${group.key}-${r.rowId}`}>
                                                    <tr className={uscenti.has(r.rowId) ? "report-sap-row-uscente" : undefined}>
                                                        <td className="report-sap-expand-col">
                                                            <button type="button" className={`report-sap-expand-btn ${isExpanded ? "open" : ""}`} onClick={() => toggleExpanded(r.rowId)} title={isExpanded ? "Comprimi dettagli" : "Mostra dettagli"} aria-expanded={isExpanded}>
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                                                            </button>
                                                        </td>
                                                        <td className="report-sap-servizio-cell">{r.percorso || "—"}</td>
                                                        <td>{r.albero || "—"}</td>
                                                        <td>{r.descrizione_commessa || "—"}</td>
                                                        <td>{r.ora_inizio || "—"}</td>
                                                        <td>{r.ora_fine || "—"}</td>
                                                        <td>{r.servizio || "—"}</td>
                                                        <td className="report-sap-action-col">
                                                            {published ? (
                                                                <div className="report-sap-azioni-riga">
                                                                    <span className="report-sap-stato-pill report-sap-stato-pill--pubblicata" title={`Post Interacta #${publishedMap[r.rowId]}`}>Pubblicata</span>
                                                                    <button type="button" className="report-sap-annulla-btn" onClick={() => handleAnnulla(r)} disabled={rowBusy || loadingPublished} title="Annulla: elimina il post da Interacta e la riga torna da convalidare">
                                                                        {busyKey === r.rowId ? "Annullo..." : "Annulla"}
                                                                    </button>
                                                                </div>
                                                            ) : conv ? (
                                                                <div className="report-sap-azioni-riga">
                                                                    {conv.stato === "errore" ? (
                                                                        <span className="report-sap-stato-pill report-sap-stato-pill--errore" title={conv.motivo || undefined}>Errore</span>
                                                                    ) : (
                                                                        <span className="report-sap-stato-pill report-sap-stato-pill--convalidata"
                                                                            title={`Convalidata${conv.da ? ` da ${conv.da}` : ""}${conv.il ? ` il ${formatDataOra(conv.il)}` : ""}: sarà pubblicata alle 20:00`}>
                                                                            ✓ Convalidata
                                                                        </span>
                                                                    )}
                                                                    <button type="button" className="report-sap-btn-secondary report-sap-annulla-conv-btn" onClick={() => handleAnnullaConvalida(r)} disabled={rowBusy} title="Togli la convalida: non verrà pubblicata">
                                                                        {busyKey === r.rowId ? "..." : "Annulla"}
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <button type="button" className="report-sap-publish-btn" onClick={() => handleConvalida(r)} disabled={rowBusy || loadingPublished || !canPublish} title={canPublish ? "Convalida: sarà pubblicata su Interacta alle 20:00" : `Impossibile convalidare:\n- ${missing.join("\n- ")}`}>
                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                                                                    {busyKey === r.rowId ? "Convalido..." : "Convalida"}
                                                                </button>
                                                            )}
                                                        </td>
                                                        <td className="report-sap-action-col">
                                                            <label className="report-sap-ignora-wrapper" onClick={(e) => e.stopPropagation()} title={r.ignora ? "Ripristina (non ignorare)" : "Ignora questa riga"}>
                                                                <input type="checkbox" checked={!!r.ignora} onChange={() => handleToggleIgnora(r)} />
                                                                <span className="report-sap-ignora-mark" aria-hidden="true" />
                                                            </label>
                                                        </td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr className={`report-sap-detail-row ${uscenti.has(r.rowId) ? "report-sap-row-uscente" : ""}`}>
                                                            <td colSpan={9}>
                                                                <div className="report-sap-detail-panel">
                                                                    <div className="report-sap-detail-panel-accent" aria-hidden="true" />
                                                                    <div className="report-sap-detail-grid">
                                                                        <div className="report-sap-detail-item"><span className="report-sap-detail-label">Codice itinerario</span><span className="report-sap-detail-value report-sap-detail-value--mono">{r.codice_itinerario || "—"}</span></div>
                                                                        <div className="report-sap-detail-item"><span className="report-sap-detail-label">Descrizione itinerario</span><span className="report-sap-detail-value">{r.descrizione_itinerario || "—"}</span></div>
                                                                        <div className="report-sap-detail-item"><span className="report-sap-detail-label">Automezzi</span><span className="report-sap-detail-value report-sap-detail-value--mono">{(r.targhe && r.targhe.length) ? r.targhe.join(", ") : ((r.veicoli_raw && r.veicoli_raw.length) ? r.veicoli_raw.join(", ") : "—")}</span></div>
                                                                        <div className="report-sap-detail-item">
                                                                            <span className="report-sap-detail-label">Operatori</span>
                                                                            <span className="report-sap-detail-value">
                                                                                {(r.operatori && r.operatori.length) ? r.operatori.map(toTitleCase).join(", ") : "—"}
                                                                                {r.operatori_non_mappati && r.operatori_non_mappati.length > 0 && (
                                                                                    <span className="report-sap-detail-warning">
                                                                                        ⚠ Senza utente Interacta, non compariranno nel post: {r.operatori_non_mappati.map(toTitleCase).join(", ")}
                                                                                    </span>
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    );
                })}
            </div>

            {confirmConvalida && (
                <div className="report-sap-confirm-overlay" onClick={() => setConfirmConvalida(null)}>
                    <div className="report-sap-confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="report-sap-confirm-icon">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                            </svg>
                        </div>
                        <h3>{confirmConvalida.scope === "all" ? "Convalida tutto" : "Convalida caposquadra"}</h3>
                        <p>
                            Stai per convalidare <strong>{confirmConvalida.items.length}</strong> servizi di <strong>{confirmConvalida.label}</strong>:
                            verranno pubblicati su Interacta alle 20:00. Quelli già convalidati o pubblicati restano come sono. Continuare?
                        </p>
                        <div className="report-sap-confirm-actions">
                            <button type="button" className="report-sap-btn-secondary" onClick={() => setConfirmConvalida(null)}>Annulla</button>
                            <button type="button" className="report-sap-btn-primary" onClick={handleConfirmConvalida}>Convalida</button>
                        </div>
                    </div>
                </div>
            )}
            </>)}

        </div>
    );
};

export default ReportSapCapisquadra;
